import React, { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Divider from '@mui/material/Divider';
import Autocomplete from '@mui/material/Autocomplete';
import Tooltip from '@mui/material/Tooltip';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PeopleIcon from '@mui/icons-material/People';
import SecurityIcon from '@mui/icons-material/Security';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import * as groupApi from '../../api/groupApi';
import * as userApi from '../../api/userApi';
import { useSnackbar } from '../../context/SnackbarContext';
import { useAuth } from '../../context/AuthContext';
import PageHeader from '../../components/common/PageHeader';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const ALL_PERMISSIONS = [
  { value: 'EDIT_PRICES',       label: 'Edit Prices',          description: 'Create and update price entries (add / update)' },
  { value: 'DELETE_PRICES',     label: 'Delete Prices',        description: 'Delete individual or bulk price records (independent of Edit Prices)' },
  { value: 'MANAGE_MASTER_DATA',label: 'Manage Master Data',   description: 'Create and edit departments, groups, materials, makes' },
  { value: 'DELETE_MASTER_DATA',  label: 'Delete Master Data',      description: 'Delete materials and makes — cascades prices and history' },
  { value: 'MANAGE_ESTIMATIONS', label: 'Manage Estimations',      description: 'Create estimations and remove individual line items' },
  { value: 'DELETE_ESTIMATIONS', label: 'Delete Estimations',      description: 'Permanently delete saved estimations from history' },
  { value: 'VIEW_AUDIT_LOGS',    label: 'View Audit Logs',         description: 'Read audit trail and price history' },
  { value: 'MANAGE_EXCEL',      label: 'Manage Excel',         description: 'Import/export Excel files' },
  { value: 'VIEW_DASHBOARD',    label: 'View Dashboard',       description: 'Access analytics dashboard' },
  { value: 'MANAGE_ACCESSORIES',          label: 'Manage Accessories',          description: 'Create and edit accessory groups and accessories master data' },
  { value: 'DELETE_ACCESSORIES',          label: 'Delete Accessories',          description: 'Delete accessories — cascades prices and history' },
  { value: 'VIEW_MATERIAL_ACCESSORIES',    label: 'View Material-Accessories',   description: 'View accessories linked to materials (read-only)' },
  { value: 'MANAGE_MATERIAL_ACCESSORIES', label: 'Manage Material-Accessories', description: 'Create and remove material-accessory relationships' },
  { value: 'DOWNLOAD_MATERIAL_ACCESSORIES', label: 'Download Accessories Mapping', description: 'Download the material-accessories relationship Excel' },
  { value: 'MANAGE_HEADER_CONFIG',          label: 'Manage Header Config',          description: 'Configure column display names and order for Excel export headers' },
  { value: 'MANAGE_PERMISSIONS',            label: 'Manage Permissions',            description: 'Access and manage Permission Groups, and assign permission groups to users' },
];

const EMPTY_FORM = { name: '', description: '', permissions: [], userIds: [] };

const permColor = {
  EDIT_PRICES: 'warning',
  DELETE_PRICES: 'error',
  MANAGE_MASTER_DATA: 'info',
  DELETE_MASTER_DATA: 'error',
  MANAGE_ESTIMATIONS: 'success',
  DELETE_ESTIMATIONS: 'error',
  VIEW_AUDIT_LOGS: 'secondary',
  MANAGE_EXCEL: 'success',
  VIEW_DASHBOARD: 'primary',
  MANAGE_ACCESSORIES: 'info',
  DELETE_ACCESSORIES: 'error',
  VIEW_MATERIAL_ACCESSORIES: 'default',
  MANAGE_MATERIAL_ACCESSORIES: 'success',
  DOWNLOAD_MATERIAL_ACCESSORIES: 'primary',
  MANAGE_HEADER_CONFIG: 'warning',
  MANAGE_PERMISSIONS: 'secondary',
};

export default function GroupManagement() {
  const [groups, setGroups] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogTab, setDialogTab] = useState(0);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [defaultTarget, setDefaultTarget] = useState(null); // { id, name, step: 1 | 2 }
  const { showSnackbar } = useSnackbar();
  const { isAdmin, hasPermission } = useAuth();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [gRes, uRes] = await Promise.all([groupApi.getAll(), userApi.getAll()]);
      setGroups(gRes.data || gRes);
      setAllUsers(uRes.data || uRes);
    }
    catch (e) { showSnackbar(e.message, 'error'); }
    finally { setLoading(false); }
  }, [showSnackbar]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditItem(null); setForm(EMPTY_FORM); setDialogTab(0); setDialogOpen(true); };
  const openEdit = (g) => {
    setEditItem(g);
    setForm({
      name: g.name,
      description: g.description || '',
      permissions: g.permissions,
      userIds: (g.users || []).map((u) => u.id),
    });
    setDialogTab(0);
    setDialogOpen(true);
  };

  const togglePerm = (p) => {
    setForm((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(p) ? prev.permissions.filter((x) => x !== p) : [...prev.permissions, p],
    }));
  };

  // Permissions the current user is allowed to grant/revoke
  // Admins can grant any; non-admins can only grant what they have
  const canGrantPerm = (p) => isAdmin || hasPermission(p);

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editItem) {
        await groupApi.update(editItem.id, form);
        showSnackbar('Group updated', 'success');
      } else {
        await groupApi.create(form);
        showSnackbar('Group created', 'success');
      }
      setDialogOpen(false); load();
    } catch (e) { showSnackbar(e.message, 'error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try { await groupApi.remove(deleteTarget.id); showSnackbar('Group deleted', 'success'); setDeleteTarget(null); load(); }
    catch (e) { showSnackbar(e.message, 'error'); setDeleteTarget(null); }
  };

  const handleSetDefault = async (g) => {
    try {
      await groupApi.setDefault(g.id);
      showSnackbar(`"${g.name}" set as default group`, 'success');
      setDefaultTarget(null);
      load();
    } catch (e) {
      showSnackbar(e.message, 'error');
      setDefaultTarget(null);
    }
  };

  const selectedUsers = allUsers.filter((u) => form.userIds.includes(u.id));

  return (
    <Box>
      <PageHeader title="Groups & Permissions" subtitle="Define groups with permission sets. Assign groups to users to grant access."
        action={<Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>Add Group</Button>}
      />

      <Card>
        {loading ? <LoadingSpinner /> : (
          <Box sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Group Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Permissions</TableCell>
                <TableCell>Users</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.map((g, i) => (
                <TableRow key={g.id} hover>
                  <TableCell>{i + 1}</TableCell>
                  <TableCell fontWeight={600}>
                    {g.name}
                    {g.isDefault && <Chip label="Default" size="small" color="warning" sx={{ ml: 0.75 }} />}
                  </TableCell>
                  <TableCell sx={{ color: '#757575' }}>{g.description || '—'}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {g.permissions.length
                        ? g.permissions.map((p) => (
                          <Chip key={p} label={ALL_PERMISSIONS.find((x) => x.value === p)?.label || p} size="small" color={permColor[p] || 'default'} />
                        ))
                        : <Typography variant="caption" color="text.disabled">No permissions</Typography>}
                    </Box>
                  </TableCell>
                  <TableCell><Chip label={`${g.userCount} user(s)`} size="small" variant="outlined" /></TableCell>
                  <TableCell align="right">
                    <Tooltip title={g.isDefault ? 'Already the default group' : 'Set as default group'}>
                      <span>
                        <IconButton size="small" color="warning" disabled={g.isDefault} onClick={() => setDefaultTarget({ id: g.id, name: g.name, step: 1 })}>
                          {g.isDefault ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
                        </IconButton>
                      </span>
                    </Tooltip>
                    <IconButton size="small" onClick={() => openEdit(g)}><EditIcon fontSize="small" /></IconButton>
                    <Tooltip title={g.isDefault ? 'Cannot delete the default group' : 'Delete'}>
                      <span>
                        <IconButton size="small" color="error" disabled={g.isDefault} onClick={() => setDeleteTarget(g)}><DeleteIcon fontSize="small" /></IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {!groups.length && <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: '#9e9e9e' }}>No groups defined</TableCell></TableRow>}
            </TableBody>
          </Table>
          </Box>
        )}
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editItem ? `Edit Group: ${editItem.name}` : 'Add Group'}</DialogTitle>
        <Tabs value={dialogTab} onChange={(_, v) => setDialogTab(v)} sx={{ px: 3, borderBottom: 1, borderColor: 'divider' }}>
          <Tab icon={<SecurityIcon fontSize="small" />} iconPosition="start" label="Permissions" />
          <Tab icon={<PeopleIcon fontSize="small" />} iconPosition="start" label={`Users (${form.userIds.length})`} />
        </Tabs>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important', minHeight: 420 }}>
          {/* Tab 0: Name / Description / Permissions */}
          {dialogTab === 0 && (
            <>
              <TextField label="Group Name" fullWidth value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <TextField label="Description" fullWidth multiline rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              <Divider />
              <Typography variant="subtitle2" fontWeight={700}>Permissions</Typography>
              <CardContent sx={{ py: 0, px: 0 }}>
                <FormGroup>
                  {ALL_PERMISSIONS.map((p) => {
                    const grantable = canGrantPerm(p.value);
                    return (
                      <FormControlLabel key={p.value}
                        control={
                          <Checkbox
                            checked={form.permissions.includes(p.value)}
                            onChange={() => togglePerm(p.value)}
                            size="small"
                            disabled={!grantable}
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2" fontWeight={500} color={grantable ? 'text.primary' : 'text.disabled'}>
                              {p.label}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">{p.description}</Typography>
                          </Box>
                        }
                        sx={{ alignItems: 'flex-start', mb: 1 }}
                      />
                    );
                  })}
                </FormGroup>
              </CardContent>
            </>
          )}

          {/* Tab 1: Users */}
          {dialogTab === 1 && (
            <>
              <Typography variant="body2" color="text.secondary">
                Search and add users to this group. Assigned users are shown below.
              </Typography>

              {/* Single-select search — only shows users NOT yet assigned */}
              <Autocomplete
                key={form.userIds.length}
                options={allUsers.filter((u) => !form.userIds.includes(u.id))}
                getOptionLabel={(u) => `${u.username} (${u.email})`}
                value={null}
                onChange={(_, user) => {
                  if (user) setForm((prev) => ({ ...prev, userIds: [...prev.userIds, user.id] }));
                }}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                noOptionsText={
                  allUsers.filter((u) => !form.userIds.includes(u.id)).length === 0
                    ? 'All users are already assigned'
                    : 'No matching users'
                }
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Box>
                      <Typography variant="body2" fontWeight={500}>{option.username}</Typography>
                      <Typography variant="caption" color="text.secondary">{option.email}</Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField {...params} size="small" label="Add User" placeholder="Search by name or email…" inputProps={{ ...params.inputProps, autoComplete: 'off' }} />
                )}
              />

              {/* Assigned users list */}
              <Box>
                <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  Assigned ({form.userIds.length})
                </Typography>
                {form.userIds.length === 0 ? (
                  <Typography variant="body2" color="text.disabled" sx={{ py: 2, textAlign: 'center' }}>
                    No users assigned — search above to add
                  </Typography>
                ) : (
                  <Box sx={{ border: '1px solid #E0E0E0', borderRadius: 1, maxHeight: 220, overflow: 'auto' }}>
                    {selectedUsers.map((u, idx) => (
                      <Box
                        key={u.id}
                        sx={{
                          display: 'flex', alignItems: 'center', px: 1.5, py: 0.75,
                          borderBottom: idx < selectedUsers.length - 1 ? '1px solid #F0F0F0' : 'none',
                          '&:hover': { bgcolor: '#FAFAFA' },
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={500} noWrap>{u.username}</Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>{u.email}</Typography>
                        </Box>
                        <Tooltip title="Remove from group">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => setForm((prev) => ({ ...prev, userIds: prev.userIds.filter((id) => id !== u.id) }))}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={!form.name || saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Group"
        message={
          deleteTarget?.userCount > 0
            ? `Delete group "${deleteTarget?.name}"? ${deleteTarget.userCount} user(s) will lose the permissions granted by this group and fall back to whatever group is currently default.`
            : `Delete group "${deleteTarget?.name}"? Users in this group will lose associated permissions.`
        }
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      {/* Set Default — two-step confirmation, since the default group applies
          to every user without an explicit group assignment. */}
      <ConfirmDialog
        open={!!defaultTarget && defaultTarget.step === 1}
        title="Change Default Group"
        confirmLabel="Continue"
        confirmColor="warning"
        message={`Set "${defaultTarget?.name}" as the default group? Any user without an explicitly assigned group will inherit this group's permissions instead of the current default.`}
        onConfirm={() => setDefaultTarget((d) => ({ ...d, step: 2 }))}
        onCancel={() => setDefaultTarget(null)}
      />
      <ConfirmDialog
        open={!!defaultTarget && defaultTarget.step === 2}
        title="Confirm Default Group Change"
        confirmLabel="Set as Default"
        confirmColor="warning"
        message={`This is final: all users with no explicitly assigned group will immediately inherit "${defaultTarget?.name}". This applies as soon as you confirm — there is no separate save step after this.`}
        onConfirm={() => handleSetDefault(defaultTarget)}
        onCancel={() => setDefaultTarget(null)}
      />
    </Box>
  );
}
